/**
 * Each block's lines and boundary databases (MB's R164, 2 Nov 2026; docs/specs/project-reporting/quick-start-
 * checklist.md §11): whether a survey block has them, and what "Create the databases" makes where it has not.
 *
 * - **Where a block's databases live** (decision 868): in its own block folder's `Geosoft\Database` when that folder
 *   holds a Geosoft project (`.gpf`), as on F26044 since 30 Oct 2026. A block folder without a project of its own uses
 *   the project's one Geosoft project when there is exactly one (F26048: `F26048_Milgun\Geosoft` holds `Lines.gdb` and
 *   `Boundary_<block>.gdb` for all three blocks). Else its own Geosoft folder, if it has one.
 * - **A block has its lines database** when that folder holds `Lines.gdb` or `Lines_<block key>.gdb`, and its boundary
 *   database when it holds `Boundary.gdb` or `Boundary_<block key>.gdb` (F26048's `Boundary_Milgun_SouthEast.gdb`).
 * - **What is made** where one is missing, from the block's own flight plan folder (`<job>_<block>\1. Flight Plan`):
 *   the lines database from its `*_LNS.xyz` with each tie line's type changed from line to tie, the boundary database
 *   from its `*_BND.xyz` with a map of it (`Maps\Boundary.map`, the boundary in blue without label). Names as MB's on
 *   F26044 (`Lines.gdb`, `Boundary.gdb`, `Boundary.map`) in a block's own project; `_<block key>` names in a shared one,
 *   so blocks never share a database by accident.
 * - **The zone** is the block's own lines file's (decision 939); a block whose lines file gives none is not made.
 * - **Tie lines** are the tenant's convention (decision 115): Thomson's second digit 9 (`390010`, block 3's tie; the
 *   first digit is the block, decision 879). MB's own `Lines.gdb` of F26044 Central East has exactly those 13 as ties.
 *
 * Teslyn Field's core lists the folder (`project_files_check`) and makes the files through the Geosoft runner
 * (`geosoft_block_databases_make`); the rules are here.
 */
import type { Block } from './model.js';
import { THOMSON_CONVENTIONS, type TenantConventions } from './conventions.js';
import { utmZoneName } from './geosoft-projection.js';
import { blockFolderOf } from './qc-daily.js';

/** One block folder's Geosoft project as `project_files_check` lists it (the fields used here). */
export interface BlockDatabasesFolder {
  readonly block_folder: string;
  readonly projects: readonly string[];
  readonly databases: readonly { readonly name: string }[];
  /** The `.map` files in its `Geosoft\Maps` (absent: not listed, taken as none). */
  readonly maps?: readonly string[];
  readonly lines_epsg?: number | null;
}

/** What the rules read: the project folder's flight plan files and Geosoft folders. */
export interface BlockDatabasesFacts {
  /** `<block folder>/1. Flight Plan/<file>`, `/` separated. */
  readonly plan_files: readonly string[];
  readonly geosoft: readonly BlockDatabasesFolder[];
}

/** One block's work for "Create the databases". */
export interface BlockDatabasesFix {
  /** The block's name (`Central East Area`). */
  readonly block: string;
  /** The block's own folder, where its flight plan is (`F26044_Central_East_Area`). */
  readonly block_folder: string;
  /** The block folder whose `Geosoft` folder takes the databases (its own, or the shared project's). */
  readonly geosoft_folder: string;
  /** The zone of the block's own lines file (decision 939). */
  readonly epsg: number;
  /** `WGS 84 / UTM zone 52S`. */
  readonly zone: string;
  /** The lines database to make from the flight plan's `*_LNS.xyz` (a file name in its `1. Flight Plan`). */
  readonly lines: { readonly xyz: string; readonly name: string } | null;
  /** The boundary database to make from `*_BND.xyz`, and its map (null: a map of that name is there). */
  readonly boundary: { readonly xyz: string; readonly name: string; readonly map: string | null } | null;
}

/** Where each survey block stands. */
export interface BlockDatabasesRow {
  readonly block: string;
  /** The block's folder, or null when the project folder has none for it. */
  readonly block_folder: string | null;
  /** The folder whose Geosoft folder holds (or would hold) its databases; null when there is none. */
  readonly geosoft_folder: string | null;
  /** The lines database found (`Lines.gdb`), or null. */
  readonly lines: string | null;
  readonly boundary: string | null;
  /** Why what is missing cannot be made (no Geosoft folder, no `_LNS.xyz`, no zone); null when it can. */
  readonly cannot: string | null;
}

export interface BlockDatabasesPlan {
  readonly rows: readonly BlockDatabasesRow[];
  /** One per block with something to make. */
  readonly fixes: readonly BlockDatabasesFix[];
}

/** The tie line rule the core applies to the lines file (the tenant's convention, decision 115). */
export function tieRule(c: TenantConventions = THOMSON_CONVENTIONS): { position: number; value: string } {
  return { position: c.tieDigitPosition, value: c.tieDigitValue };
}

const lower = (s: string): string => s.toLowerCase();

/** The plan file of a block folder ending in `suffix` (`_lns.xyz`), the `_fnl_` one first; null when none. */
function planFile(planFiles: readonly string[], folder: string, suffix: string): string | null {
  const prefix = `${lower(folder)}/1. flight plan/`;
  const names = planFiles
    .filter((p) => lower(p).startsWith(prefix) && !p.slice(prefix.length).includes('/'))
    .map((p) => p.slice(prefix.length))
    .filter((n) => lower(n).endsWith(suffix))
    .sort((a, b) => Number(!/_fnl_/i.test(a)) - Number(!/_fnl_/i.test(b)) || a.localeCompare(b));
  return names[0] ?? null;
}

/** The first of `names` (any case) among a folder's databases. */
function found(folder: BlockDatabasesFolder | undefined, names: readonly string[]): string | null {
  if (!folder) return null;
  const want = new Set(names.map(lower));
  return folder.databases.find((d) => want.has(lower(d.name)))?.name ?? null;
}

/** The lines and boundary databases of every live survey block, and what to make where they are missing. */
export function geosoftBlockDatabases(
  blocks: readonly Pick<Block, 'name' | 'key' | 'kind' | 'epsg' | 'deleted_at'>[],
  facts: BlockDatabasesFacts,
): BlockDatabasesPlan {
  const survey = blocks.filter((b) => b.kind === 'survey' && b.deleted_at === null);
  const geo = facts.geosoft.filter((g) => !['teslyn', 'survera'].includes(lower(g.block_folder)));
  const planFolders = [...new Set(facts.plan_files.map((p) => p.split('/')[0]!).filter(Boolean))];
  const names = [...new Set([...planFolders, ...geo.map((g) => g.block_folder)])];
  const withProject = geo.filter((g) => g.projects.length > 0);
  const rows: BlockDatabasesRow[] = [];
  const fixes: BlockDatabasesFix[] = [];
  for (const b of survey) {
    const folder = blockFolderOf(b, names);
    const own = folder ? geo.find((g) => lower(g.block_folder) === lower(folder)) : undefined;
    // a block folder without a project of its own uses the project's one Geosoft project (F26048)
    const shared =
      (!own || own.projects.length === 0) && withProject.length === 1 && withProject[0] !== own
        ? withProject[0]
        : undefined;
    const ownLines = found(own, ['Lines.gdb', `Lines_${b.key}.gdb`]);
    const ownBoundary = found(own, ['Boundary.gdb', `Boundary_${b.key}.gdb`]);
    const lines = ownLines ?? found(shared, [`Lines_${b.key}.gdb`, 'Lines.gdb']);
    const boundary = ownBoundary ?? found(shared, [`Boundary_${b.key}.gdb`]);
    const where = (hit: string | null, ownHit: string | null): string | null =>
      hit === null ? null : ownHit !== null ? own!.block_folder : shared!.block_folder;
    // decision 868: what is missing goes into the block's own Geosoft folder, else the shared project's
    const home = own ?? shared;
    if (lines && boundary) {
      rows.push({
        block: b.name,
        block_folder: folder,
        geosoft_folder: where(lines, ownLines) ?? where(boundary, ownBoundary),
        lines,
        boundary,
        cannot: null,
      });
      continue;
    }
    // a block of a shared project takes its own names, so two blocks never write one database
    const suffix = home !== undefined && home !== own ? `_${b.key}` : '';
    const lnsXyz = folder ? planFile(facts.plan_files, folder, '_lns.xyz') : null;
    const bndXyz = folder ? planFile(facts.plan_files, folder, '_bnd.xyz') : null;
    // decision 939: the zone of the block's own lines file, else the block's row (read from that file at import)
    const epsg = utmZoneName(own?.lines_epsg) !== null ? own!.lines_epsg! : (b.epsg ?? null);
    const zone = utmZoneName(epsg);
    const why: string[] = [];
    if (!folder) why.push('the project folder has no folder for it');
    else if (!home) why.push(`${folder} has no Geosoft folder`);
    else if (zone === null) why.push(`${folder}'s lines file (_LNS.lns) gives no zone`);
    if (folder && home && !lines && !lnsXyz) why.push(`${folder}\\1. Flight Plan has no _LNS.xyz`);
    if (folder && home && !boundary && !bndXyz) why.push(`${folder}\\1. Flight Plan has no _BND.xyz`);
    const ready = folder !== null && home !== undefined && zone !== null;
    const canLines = ready && !lines && lnsXyz !== null;
    const canBoundary = ready && !boundary && bndXyz !== null;
    rows.push({
      block: b.name,
      block_folder: folder,
      geosoft_folder: home?.block_folder ?? null,
      lines,
      boundary,
      cannot: why.length > 0 ? why.join('; ') : null,
    });
    if (!canLines && !canBoundary) continue;
    const mapName = `Boundary${suffix}.map`;
    const mapThere = (home!.maps ?? []).some((m) => lower(m) === lower(mapName));
    fixes.push({
      block: b.name,
      block_folder: folder!,
      geosoft_folder: home!.block_folder,
      epsg: epsg!,
      zone: zone!,
      lines: canLines ? { xyz: lnsXyz!, name: `Lines${suffix}.gdb` } : null,
      boundary: canBoundary
        ? { xyz: bndXyz!, name: `Boundary${suffix}.gdb`, map: mapThere ? null : mapName }
        : null,
    });
  }
  return { rows, fixes };
}

/** One line per block for the confirmation: what is made, from which file, in which zone. */
export function blockDatabasesList(fixes: readonly BlockDatabasesFix[]): string[] {
  return fixes.map((f) => {
    const parts: string[] = [];
    if (f.lines) parts.push(`${f.lines.name} from ${f.lines.xyz}, tie lines set to tie`);
    if (f.boundary)
      parts.push(
        `${f.boundary.name}${f.boundary.map ? ` and Maps\\${f.boundary.map}` : ''} from ${f.boundary.xyz}`,
      );
    const where =
      f.geosoft_folder === f.block_folder
        ? f.block_folder
        : `${f.block_folder} (in ${f.geosoft_folder}\\Geosoft)`;
    return `${where}: ${parts.join('; ')}; ${f.zone.replace('WGS 84 / UTM zone ', 'zone ')}.`;
  });
}

/** The question "Create the databases" asks before Oasis montaj runs. */
export function blockDatabasesQuestion(
  fixes: readonly BlockDatabasesFix[],
  c: TenantConventions = THOMSON_CONVENTIONS,
): string {
  const n = fixes.length;
  const nth = ORDINAL[c.tieDigitPosition] ?? `number ${c.tieDigitPosition}`;
  return `Make the missing lines and boundary databases of ${n === 1 ? fixes[0]!.block : `these ${n} blocks`} from ${n === 1 ? 'its' : 'their'} flight plan? Oasis montaj runs headless, about a minute per block. Each tie line's type is set to tie (the ${nth} digit of its number is ${c.tieDigitValue}), and each database takes the zone of its own block's lines file. Nothing already there is changed.`;
}

const ORDINAL: Readonly<Record<number, string>> = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  5: 'fifth',
  6: 'sixth',
};

/** What the core reports for one block (`geosoft_block_databases_make`). */
export interface BlockDatabasesMade {
  readonly block_folder: string;
  /** Files written into the project folder. */
  readonly made: readonly string[];
  /** Files that were there by then, and kept. */
  readonly kept: readonly string[];
  readonly lines: number | null;
  readonly ties: number | null;
  readonly ties_missed: readonly string[];
  readonly boundary_lines: number | null;
  readonly zone: string | null;
  /** Why the block's databases were not made, when they were not. */
  readonly error: string | null;
}

/** What happened, in words, after the run. */
export function blockDatabasesOutcome(made: readonly BlockDatabasesMade[]): string {
  const name = (p: string): string => p.split(/[\\/]/).pop() ?? p;
  return made
    .map((m) => {
      if (m.error) return `${m.block_folder}: not made. ${m.error}`;
      const gdbs = m.made.filter((p) => /\.(gdb|map)$/i.test(p)).map(name);
      const bits: string[] = [];
      if (gdbs.length > 0) bits.push(`${gdbs.join(', ')} made`);
      if (m.lines !== null) bits.push(`${m.lines} lines, ${m.ties ?? 0} of them tie lines`);
      if (m.boundary_lines !== null) bits.push(`${m.boundary_lines} boundary edges`);
      if (m.zone) bits.push(`in ${m.zone}`);
      if (m.ties_missed.length > 0)
        bits.push(
          `${m.ties_missed.length} tie lines not set to tie (${m.ties_missed.slice(0, 4).join(', ')})`,
        );
      const kept = m.kept.map(name).filter((n) => /\.(gdb|map)$/i.test(n));
      if (kept.length > 0) bits.push(`${kept.join(', ')} kept as found`);
      return `${m.block_folder}: ${bits.join('; ')}.`;
    })
    .join(' ');
}
